/**
 * XIRR — annualised internal rate of return for irregular cash flows.
 * Same semantics as Excel's XIRR(): finds the rate r such that NPV = 0
 * when each cash flow is discounted by its actual time offset in years
 * from the first flow.
 *
 *  cashflows: [{ amount: number, date: Date }]
 *    - negative amounts = money out (buys, contributions)
 *    - positive amounts = money in (sells, dividends, current value)
 *
 * Returns the annualised rate as a decimal (0.12 = +12%), or null if it
 * doesn't converge or the input is invalid.
 */
export function xirr(cashflows, guess = 0.1) {
  if (!Array.isArray(cashflows) || cashflows.length < 2) return null;

  const sorted = [...cashflows]
    .filter(cf => Number.isFinite(cf?.amount) && cf?.date instanceof Date && !Number.isNaN(cf.date.getTime()))
    .sort((a, b) => a.date.getTime() - b.date.getTime());

  if (sorted.length < 2) return null;

  const hasPos = sorted.some(cf => cf.amount > 0);
  const hasNeg = sorted.some(cf => cf.amount < 0);
  if (!hasPos || !hasNeg) return null;

  const t0 = sorted[0].date.getTime();
  const yearsOf = (d) => (d.getTime() - t0) / (365.25 * 86_400_000);

  // NPV at rate r
  const npv = (r) => sorted.reduce(
    (sum, cf) => sum + cf.amount / Math.pow(1 + r, yearsOf(cf.date)),
    0
  );
  // Derivative of NPV w.r.t. r
  const dnpv = (r) => sorted.reduce(
    (sum, cf) => sum - (yearsOf(cf.date) * cf.amount) / Math.pow(1 + r, yearsOf(cf.date) + 1),
    0
  );

  // Newton-Raphson
  let rate = guess;
  const MAX_ITER = 100;
  const TOL = 1e-7;

  for (let i = 0; i < MAX_ITER; i++) {
    const f = npv(rate);
    if (Math.abs(f) < TOL) return rate;
    const df = dnpv(rate);
    if (!Number.isFinite(df) || Math.abs(df) < 1e-12) break;
    const next = rate - f / df;
    if (!Number.isFinite(next)) break;
    if (Math.abs(next - rate) < TOL) return next;
    // Keep rate in sensible bounds — XIRR is undefined at r = -1
    rate = Math.max(-0.999, Math.min(next, 1000));
  }

  // Newton failed → bisection fallback on [-0.99, 10]
  let lo = -0.99, hi = 10;
  let fLo = npv(lo), fHi = npv(hi);
  if (!Number.isFinite(fLo) || !Number.isFinite(fHi)) return null;
  if (fLo * fHi > 0) return null; // no sign change in interval
  for (let i = 0; i < 200; i++) {
    const mid = (lo + hi) / 2;
    const fMid = npv(mid);
    if (Math.abs(fMid) < TOL || (hi - lo) / 2 < TOL) return mid;
    if (fLo * fMid < 0) { hi = mid; fHi = fMid; }
    else                { lo = mid; fLo = fMid; }
  }
  return null;
}
